import React, { FC } from 'react';
import { Typography, Space, Button, Spin, Row, Col, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import featuresWithDefaults from '~/featuresWithDefaults';
import { isEqual } from 'lodash';
import cx from 'classnames';

import { Form, LegalTypeInput, IndustryInput, MCCInput } from '~/components';
import { useCurrentBusinessKyc, useKycPage } from '~/hooks/kyc';
import { useStatefulForm } from '~/hooks/form';
import { formWidth } from './utils';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Business } from '~/types';

const CompanyDetails: FC<any> = (props) => {
  const { className } = props;
  const [t] = useTranslation('kyc');
  const [tp] = useTranslation('profile');
  const [tt] = useTranslation('translation');

  const { currentBusiness, setCurrentBusiness, isLoading } = useCurrentBusinessKyc();
  const countryId = currentBusiness?.country;

  const { form, handleFormChange } = useStatefulForm<Business>(currentBusiness);

  const kycPage = useKycPage();
  const onFinish = async (values: any) => {
    const newBusiness = { ...currentBusiness, ...values };
    if (isEqual(newBusiness, currentBusiness)) {
      kycPage.goNext();
      return;
    }
    const curLegalType = currentBusiness?.legalType;
    const newLegalType = newBusiness?.legalType;
    if (curLegalType !== newLegalType) {
      Modal.confirm({
        title: t('Do you want to proceed?'),
        icon: <ExclamationCircleOutlined />,
        content: t('You have changed legal type. This may lead to the loss of attached documents.'),
        okText: tt('confirm'),
        cancelText: tt('cancel'),
        onOk: () => {
          setCurrentBusiness({ ...newBusiness, primaryDocument: {}, documents: [] });
          kycPage.goNext();
        },
      });
    } else {
      setCurrentBusiness(newBusiness);
      kycPage.goNext();
    }
  };

  return (
    <Space direction="vertical" size="large" className={cx(className, 'Step-Container')}>
      <Typography.Title level={3}>{t('Company details')}</Typography.Title>
      {isLoading ? (
        <Spin />
      ) : (
        <Form
          form={form}
          onValuesChange={(_, newValues) => handleFormChange(newValues)}
          onFinish={onFinish}
          layout="vertical"
        >
          <Row gutter={[rowGap, 0]}>
            <Col flex="none" style={{ width: formWidth.width + rowGap }}>
              {featuresWithDefaults.kyc.extraFields.legalType && (
                <Form.Item label={tp('legalType')} name="legalType" rules={[{ required: true }]}>
                  <LegalTypeInput countryId={countryId} placeholder="" />
                </Form.Item>
              )}
              {featuresWithDefaults.kyc.extraFields.legalName && (
                <Form.Item label={tp('legalName')} name="legalName" rules={[{ required: true }]}>
                  <Input disabled />
                </Form.Item>
              )}
              {featuresWithDefaults.kyc.extraFields.tradeName && (
                <Form.Item label={tp('tradeName')} name="tradeName" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              )}
              {featuresWithDefaults.kyc.extraFields.industry && (
                <Form.Item label={tp('industry')} name="industry" rules={[{ required: true }]}>
                  <IndustryInput />
                </Form.Item>
              )}
              {featuresWithDefaults.kyc.extraFields.merchantCategoryCode && (
                <Form.Item
                  name="merchantCategoryCode"
                  label={tp('merchantCategoryCode')}
                  rules={[{ required: true }]}
                >
                  <MCCInput />
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col>
              <Form.Item noStyle>
                <Button type="primary" htmlType="submit" size="large">
                  {t('translation:Next')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Space>
  );
};
const rowGap = 16;

export default CompanyDetails;
